/**
 * Utility functions for searching icons from Iconify API
 * https://iconify.design/docs/api/
 */

interface IconifySearchResult {
  icons: string[]
  total: number
  limit: number
  start: number
  collections: Record<string, number>
}

// Fallback list of popular Lucide icons if API is unavailable
const FALLBACK_LUCIDE_ICONS = [
  'lucide-house',
  'lucide-chart-bar',
  'lucide-chart-line',
  'lucide-chart-pie',
  'lucide-database',
  'lucide-file-text',
  'lucide-folder',
  'lucide-globe',
  'lucide-users',
  'lucide-settings',
  'lucide-bell',
  'lucide-mail',
  'lucide-calendar',
  'lucide-clock',
  'lucide-star',
  'lucide-heart',
  'lucide-bookmark',
  'lucide-tag',
  'lucide-filter',
  'lucide-search',
  'lucide-arrow-right',
  'lucide-arrow-left',
  'lucide-arrow-up',
  'lucide-arrow-down',
  'lucide-check',
  'lucide-x',
  'lucide-plus',
  'lucide-minus',
  'lucide-edit',
  'lucide-trash',
  'lucide-copy',
  'lucide-download',
  'lucide-upload',
  'lucide-share',
  'lucide-link',
  'lucide-external-link',
  'lucide-image',
  'lucide-video',
  'lucide-music',
  'lucide-file',
  'lucide-folder-open',
  'lucide-archive',
  'lucide-inbox',
  'lucide-send',
  'lucide-message-square',
  'lucide-phone',
  'lucide-camera',
  'lucide-mic',
  'lucide-headphones',
  'lucide-monitor',
  'lucide-smartphone',
  'lucide-tablet',
  'lucide-laptop',
  'lucide-server',
  'lucide-cloud',
  'lucide-cloud-upload',
  'lucide-cloud-download',
  'lucide-wifi',
  'lucide-bluetooth',
  'lucide-battery',
  'lucide-zap',
  'lucide-sun',
  'lucide-moon',
  'lucide-umbrella',
  'lucide-droplet',
  'lucide-flame',
  'lucide-snowflake',
  'lucide-wind',
  'lucide-map',
  'lucide-map-pin',
  'lucide-navigation',
  'lucide-compass',
  'lucide-flag',
  'lucide-home',
  'lucide-building',
  'lucide-store',
  'lucide-shopping-cart',
  'lucide-credit-card',
  'lucide-wallet',
  'lucide-coins',
  'lucide-trending-up',
  'lucide-trending-down',
  'lucide-bar-chart',
  'lucide-line-chart',
  'lucide-pie-chart',
  'lucide-activity',
  'lucide-target',
  'lucide-award',
  'lucide-trophy',
  'lucide-medal',
  'lucide-gift',
  'lucide-package',
  'lucide-box',
  'lucide-layers',
  'lucide-grid',
  'lucide-list',
  'lucide-layout',
  'lucide-sidebar',
  'lucide-menu',
  'lucide-more-horizontal',
  'lucide-more-vertical',
  'lucide-ellipsis',
  'lucide-dot',
  'lucide-circle',
  'lucide-square',
  'lucide-triangle',
  'lucide-hexagon',
  'lucide-octagon',
]

// Cache for search results
const searchCache = new Map<string, string[]>()

/**
 * Search icons from Iconify API
 * @param query - Search query (e.g., "house", "chart")
 * @returns Promise with array of icon names in format "collection-name" (e.g., "lucide-house")
 */
export async function searchIcons(query: string): Promise<string[]> {
  if (!query || query.trim().length === 0) {
    return []
  }

  const normalizedQuery = query.trim().toLowerCase()

  // Check cache first
  if (searchCache.has(normalizedQuery)) {
    return searchCache.get(normalizedQuery)!
  }

  try {
    // Use Iconify API to search icons
    // API endpoint: https://api.iconify.design/search?query={query}&limit=50
    const response = await fetch(
      `https://api.iconify.design/search?query=${encodeURIComponent(normalizedQuery)}&limit=50`,
    )

    if (!response.ok) {
      throw new Error(`Iconify API returned ${response.status}`)
    }

    const data: IconifySearchResult = await response.json()

    // Extract icon names from results
    // Icons are in format "collection:name", we want "collection-name"
    // Convert icon names, then sort so "lucide-" icons come first
    const icons = data.icons
      .map((icon) => {
        // Handle both "collection:name" and "collection-name" formats
        if (icon.includes(':')) {
          return icon.replace(':', '-')
        }
        return icon
      })
      .sort((a, b) => {
        const isLucideA = a.startsWith('lucide-') ? 0 : 1
        const isLucideB = b.startsWith('lucide-') ? 0 : 1
        return isLucideA - isLucideB
      })

    // Cache the results
    searchCache.set(normalizedQuery, icons)

    return icons
  } catch (error) {
    console.warn('Failed to fetch icons from Iconify API:', error)
    // Fallback to filtering from static list
    return filterFallbackIcons(normalizedQuery)
  }
}

/**
 * Filter fallback icons based on query
 */
function filterFallbackIcons(query: string): string[] {
  return FALLBACK_LUCIDE_ICONS.filter((icon) => icon.includes(query))
}

/**
 * Convert icon name from "collection-name" format to "i-collection-name" format for Nuxt UI
 * @param iconName - Icon name in format "lucide-house"
 * @returns Icon name in format "i-lucide-house"
 */
export function formatIconForUI(iconName: string): string {
  if (!iconName) return ''
  // If already in correct format, return as is
  if (iconName.startsWith('i-')) {
    return iconName
  }
  // Otherwise, add "i-" prefix
  return `i-${iconName}`
}

/**
 * Convert icon name from "i-collection-name" format back to "collection-name" format
 * @param iconName - Icon name in format "i-lucide-house"
 * @returns Icon name in format "lucide-house"
 */
export function formatIconForStorage(iconName: string | undefined): string {
  if (!iconName) return ''
  // Remove "i-" prefix if present
  if (iconName.startsWith('i-')) {
    return iconName.substring(2)
  }
  return iconName
}
